/* eslint-disable no-underscore-dangle */
import cn from 'classnames';
import Image from 'next/image';
import { useRef, useEffect, useMemo } from 'react';
import { Scrollbar } from 'react-scrollbars-custom';

import type { TVideo } from '../../../../model';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import styles from './all.module.css';

type TProps = {
  chords: TVideo['chords'];
  activePosition?: number;
  onChange: (val: number) => void;
};

const WIDTH = 63;

const LANG_FILE = {
  min: 'm',
  min7: 'm7',
  min6: 'm6',
  min9: 'm9',
  min11: 'm11',
  min13: 'm13',
  hdim7: 'dim7',
};
const ALIASES = {
  'B#': 'C',
  Db: 'C#',
  Eb: 'D#',
  Fb: 'E',
  'E#': 'F',
  Gb: 'F#',
  Ab: 'G#',
  Bb: 'A#',
  Cb: 'B',
};

function getFileName(x11: string, x12: string, x2: string) {
  let chord = `${x11}${x12 || ''}`;

  if (ALIASES[chord]) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    chord = ALIASES[chord];
  }

  chord = `${chord}${x2 ? LANG_FILE[x2] || x2 : ''}`.replace('#', '_sh_');

  return `/chords/${chord}.jpg`;
}

// https://nextjs.org/docs/pages/building-your-application/optimizing/static-assets
export const All = ({ chords, activePosition, onChange }: TProps) => {
  const ref = useRef<Scrollbar>();
  const chordWrap = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const center = Math.round((ref.current?.contentElement?.clientWidth || 0) / 2 / WIDTH);
    ref.current?.contentElement?.parentElement?.scrollTo({
      behavior: 'smooth',
      left: activePosition ? (activePosition - center) * (WIDTH + 4) : undefined,
      top: 0,
    });
  }, [activePosition]);

  const previewChords = useMemo(() => {
    return chords?.reduce<{
      result: {
        position: number;
        start: string | number;
        end: string | number;
        chord: string;
      }[];
      prev: string | null;
    }>(
      (acc, item) => {
        if (acc.prev === item.chord) {
          acc.result.push({ ...item, chord: 'N' });
        } else {
          acc.prev = item.chord;
          acc.result.push(item);
        }
        return acc;
      },
      {
        result: [],
        prev: null,
      },
    ).result;
  }, [chords]);

  /**
   * Список аккордов, со схлопнутыми повторениями
   */
  const chordsList = useMemo(() => {
    if (!chords) {
      return undefined;
    }
    let index = 0;

    return chords.reduce<{
      result: { chord: string; positions: number[]; index: number; start: string | number }[];
      prev: { chord: string; positions: number[]; index: number; start: string | number } | null;
    }>(
      (acc, { chord, start, position }) => {
        if (acc.prev?.chord !== chord) {
          if (chord !== 'N') {
            index += 1;
          }
          acc.prev = { chord, start, positions: [position], index };
          acc.result.push(acc.prev);
        } else if (acc.prev) {
          acc.prev.positions.push(position);
        }
        return acc;
      },
      {
        result: [],
        prev: null,
      },
    ).result;
  }, [chords]);

  const chordsListShift = useMemo(() => {
    if (!activePosition) {
      return undefined;
    }
    const activeIndex = chordsList?.find(({ positions }) =>
      positions.includes(activePosition),
    )?.index;
    const shift = activeIndex && activeIndex > 1 ? (activeIndex - 1) * (126 + 28) : 0;

    if (
      chordWrap.current &&
      chordWrap.current.scrollWidth - shift < chordWrap.current?.offsetWidth
    ) {
      return chordWrap.current.scrollWidth - chordWrap.current.offsetWidth + 14;
    }
    return shift;
  }, [chordsList, activePosition]);

  return (
    <div className={styles.block}>
      <div className={styles.bg}>
        <Scrollbar
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          ref={ref}
          style={{ height: 72 }}
          noScrollY
        >
          <div className={styles.wrap}>
            {previewChords?.map(({ chord, start, position }, i) => {
              const [x1, x2] = chord.split(':');
              const [x11, x12] = x1.split('');

              return (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div
                  id={`position_${position}`}
                  key={chord + i.toString()}
                  className={cn(styles.cell, {
                    [styles.active]: i + 1 === activePosition,
                  })}
                  onClick={() => {
                    onChange(Number(start));
                  }}
                >
                  <div className={styles.chord}>
                    {x11 !== 'N' ? x11 : null}
                    {x12 && <small>{x12}</small>}
                    <sup className={styles.small}>{x2}</sup>
                  </div>
                </div>
              );
            })}
          </div>
        </Scrollbar>
      </div>
      <div
        ref={chordWrap}
        className={styles.chords}
        style={{
          // transform: `translateX(-${indexOnlyChord && indexOnlyChord > 1 ? (indexOnlyChord - 1) * (123 + 28) - 123 / 2 : 0}px)`,
          transform: `translateX(-${chordsListShift}px)`,
        }}
      >
        {chordsList?.map(({ chord, start, positions }) => {
          const [x1, x2] = chord.split(':');
          const [x11, x12] = x1.split('');

          if (x11 === 'N') {
            return null;
          }

          const isActive = activePosition && positions.includes(activePosition);

          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              key={positions.join()}
              onClick={() => {
                onChange(Number(start));
              }}
              className={cn(styles.chordImg, {
                [styles.activeСhordImg]: isActive,
              })}
            >
              {x11 !== 'N' && (
                <>
                  <Image src={getFileName(x11, x12, x2)} width={126} height={130} alt={chord} />
                  <p className={styles.chordImgTitle}>
                    {x11}
                    {x12 && <small>{x12}</small>}
                    {x2 && <sup className={styles.small}>{x2}</sup>}
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
